import React, { useState, useEffect, useCallback } from "react";
import {
    Grid,
    Box,
    Paper,
    makeStyles,
    TableContainer,
    Table,
    TableCell,
    TableRow,
    TableHead,
    TableBody,
    CircularProgress,
} from "@material-ui/core";

import * as actions from "./actions";
import moment from "moment";
import DeleteIcon from "@material-ui/icons/Delete";
import { getCurrentUserId, getCurrentUserOrganizationId, toastError, toastSuccess } from "../../../utils";
import CustomPagination from "../../../components/Common/CustomPagination";
import CustomConfirmModal from "../../../components/Common/CustomConfirmModal";
import ShareIcon from '@material-ui/icons/Share';
import ShareWithOrg from "../../../components/Common/ShareWithOrg";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import SearchBar from "../../../common/Search";
import { LuArrowDown, LuArrowDownUp, LuArrowUp } from "react-icons/lu";
import _ from "lodash";
import CustomTabs from "../../../common/Tabs";

const useStyles = makeStyles({
    root: {
        width: "fit-content",
    },
    // Beats the global universal-table cell padding (0.5px !important) to give rows ~23px height.
    tableContainer: {
        "& .universal-table tbody tr .MuiTableCell-root, & .universal-table tbody tr td": {
            paddingTop: "3px !important",
            paddingBottom: "3px !important",
        },
    },
});

function VesselAnalysisListing(props) {
    const { loader, setLoader = () => { } } = props;
    const classes = useStyles();

    const [value, setValue] = useState(1);

    const [savedResults, setSavedResults] = useState([]);
    const [allSavedResults, setAllSavedResults] = useState([]);

    const [hasFetchedUser, setHasFetchedUser] = useState(false);
    const [hasFetchedOrg, setHasFetchedOrg] = useState(false);
    const [isInitialUserLoading, setIsInitialUserLoading] = useState(true);
    const [isInitialOrgLoading, setIsInitialOrgLoading] = useState(true);

    const [myFilters, setMyFilters] = useState({
        offset: 1,
        limit: 50,
        sortBy: "createdDate",
        sortDirection: "desc",
    });
    const [allFilters, setAllFilters] = useState({
        offset: 1,
        limit: 50,
        sortBy: "createdDate",
        sortDirection: "desc",
    });

    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState("");

    const [isShareConfirm, setShareConfirm] = useState(false);
    const [getSingleVesselData, setSingleVesselData] = useState("");
    const [shareLabel, setShareLabel] = useState("");

    const organizationId = getCurrentUserOrganizationId();
    const [searchValue, setSearchValue] = useState("");

    const onSearchVessel = (search) => {
        getAllSavedResults(search);
        getAllOrgSavedResults(search);
    };

    const delaySearch = useCallback(
        _.debounce((data) => onSearchVessel(data), 500),
        []
    );

    const onSearchChange = (e) => {
        setSearchValue(e.target.value);
        delaySearch(e.target.value);
    };

    useEffect(() => {
        const fetchInitial = async () => {
            setLoader(true);
            await Promise.all([
                getAllSavedResults(),
                getAllOrgSavedResults()
            ]);
            setLoader(false);
        };
        fetchInitial();
    }, []);

    const getAllSavedResults = async (search, filters) => {
        const currentFilters = filters || myFilters;
        if (!hasFetchedUser && (!savedResults || savedResults.length === 0)) {
            setIsInitialUserLoading(true);
        }
        try {
            const res = await actions.getVesselCargoAnalysisList({ userId: getCurrentUserId(), search: search, ...currentFilters });
            setIsInitialUserLoading(false);
            setHasFetchedUser(true);
            setSavedResults(res?.data?.list || []);
            setMyFilters((prev) => ({
                ...prev,
                ...currentFilters,
                totalPages: res?.data?.totalItems,
                offset: res?.data?.currentPage,
                pageCount: res?.data?.totalPages,
                limit: res?.data?.perPage
            }));
        } catch (err) {
            setIsInitialUserLoading(false);
            setHasFetchedUser(true);
            toastError(err?.data?.message);
        }
    };

    const getAllOrgSavedResults = async (search, filters) => {
        const currentFilters = filters || allFilters;
        if (!hasFetchedOrg && (!allSavedResults || allSavedResults.length === 0)) {
            setIsInitialOrgLoading(true);
        }
        try {
            const res = await actions.getVesselCargoAnalysisList({ organizationId: organizationId, search: search, ...currentFilters });
            setIsInitialOrgLoading(false);
            setHasFetchedOrg(true);
            setAllSavedResults(res?.data?.list || []);
            setAllFilters((prev) => ({
                ...prev,
                ...currentFilters,
                totalPages: res?.data?.totalItems,
                offset: res?.data?.currentPage,
                pageCount: res?.data?.totalPages,
                limit: res?.data?.perPage
            }));
        } catch (err) {
            setIsInitialOrgLoading(false);
            setHasFetchedOrg(true);
            toastError(err?.data?.message);
        }
    };

    const handleSortClick = (sortKey, isAll) => {
        const currentFilters = isAll ? allFilters : myFilters;
        let newSortDirection = "asc";

        if (currentFilters.sortBy === sortKey) {
            if (currentFilters.sortDirection === "asc") newSortDirection = "desc";
            else if (currentFilters.sortDirection === "desc") {
                newSortDirection = sortKey === "createdDate" ? "asc" : "";
            }
        } else {
            newSortDirection = sortKey === "createdDate" ? "desc" : "asc";
        }

        let apiFilters = { ...currentFilters, sortBy: sortKey, sortDirection: newSortDirection, offset: 1 };
        if (!newSortDirection) {
            apiFilters.sortBy = "createdDate";
            apiFilters.sortDirection = "desc";
        }

        if (isAll) {
            getAllOrgSavedResults(searchValue, apiFilters);
        } else {
            getAllSavedResults(searchValue, apiFilters);
        }
    };

    const onChangeMyPagination = (type = "") => {
        let newMyFilters = {};
        if (type === "inc") {
            newMyFilters = { ...myFilters, offset: myFilters.offset + 1 };
        } else if (type === "first") {
            newMyFilters = { ...myFilters, offset: 1 };
        } else {
            if (myFilters.offset === 1) return;
            newMyFilters = { ...myFilters, offset: myFilters.offset - 1 };
        }
        actions.getVesselCargoAnalysisList({ ...newMyFilters, userId: getCurrentUserId(), search: searchValue })
            .then((res) => {
                setMyFilters({
                    totalPages: res?.data?.totalItems,
                    offset: res?.data?.currentPage,
                    pageCount: res?.data?.totalPages,
                    limit: res?.data?.perPage
                });
                setSavedResults(res?.data?.list || []);
            })
            .catch(() => {
            });
    };

    const onChangeAllPagination = (type = "") => {
        let newallFilters = {};
        if (type === 'inc') {
            newallFilters = { ...allFilters, offset: allFilters.offset + 1 };
        } else if (type === 'first') {
            newallFilters = { ...allFilters, offset: 1 };
        } else {
            if (allFilters.offset === 1) return;
            newallFilters = { ...allFilters, offset: allFilters.offset - 1 };
        }
        actions.getVesselCargoAnalysisList({ ...newallFilters, organizationId: organizationId, search: searchValue })
            .then((res) => {
                setAllFilters({
                    totalPages: res?.data?.totalItems,
                    offset: res?.data?.currentPage,
                    pageCount: res?.data?.totalPages,
                    limit: res?.data?.perPage
                });
                setAllSavedResults(res?.data?.list || []);
            })
            .catch(() => {
            });
    };

    const onDeleteVesselAnalysis = () => {
        actions.deleteVesselCargoAnalysis(deleteId)
            .then(() => {
                toastSuccess("Result deleted successfully");
                setDeleteModal(false);
                setDeleteId("");
                getAllSavedResults();
            })
            .catch(() => {
                toastError();
            });
    };

    const onClickDeleteAddress = (id) => {
        setDeleteId(id);
        setDeleteModal(true);
    };

    const onShareOrgSaveClick = async () => {
        if (!organizationId) {
            toastError("You are not part of any organization!");
            return;
        }
        if (!shareLabel || !getSingleVesselData) return;

        let { _id, ...data } = getSingleVesselData;
        data.access = "public";
        data.organizationId = organizationId;
        data.name = shareLabel;
        data.userId = null;

        try {
            await actions.createVesselCargoAnalysis(data);
            setShareConfirm(false);
            setShareLabel("");
            getAllOrgSavedResults();
        } catch (err) {
            toastError(err?.data?.message || "Failed to share analysis");
        }
    };

    const onShareOrgClick = (item) => {
        setShareConfirm(!isShareConfirm);
        setSingleVesselData(item ? item : "");
        setShareLabel("");
    };

    const handleDateRangeChange = async (newDateRange) => {
        const { startDate, endDate } = newDateRange;
        const formattedStartDate = startDate?.toISOString() || '';
        const formattedEndDate = endDate?.toISOString() || '';

        try {
            const { data } = await actions.getVesselCargoAnalysisList({
                userId: getCurrentUserId(),
                startDate: formattedStartDate,
                endDate: formattedEndDate
            });
            setSavedResults(data.list);
        } catch (err) {
            toastError("Failed to fetch filtered data");
        }
    };

    const tabs = [
        { key: 1, label: "My Estimation analysis", tooltip: "Displays Estimation analysis entries created by you." },
        { key: 2, label: "All Estimation analysis", tooltip: "Displays all Estimation analysis entries shared with your organization" },
    ];

    return (
        <Grid container alignContent="flex-start" className="min-h-[calc(100vh-106px)]">
            <Grid item xs={12}>
                <Box display="flex" alignItems="center" position="relative" width="100%">
                    <Box>
                        <CustomTabs tabs={tabs} active={value} setActive={setValue} />
                    </Box>
                    <Box position="absolute" left="50%" sx={{ transform: 'translateX(-50%)', display: { xs: 'none', md: 'block' } }}>
                        <div className="desktop-searchbar">
                            <SearchBar
                                onDateRangeChange={handleDateRangeChange}
                                placeholder={"Search with Analysis Name..."}
                                setSearchValue={onSearchChange}
                                searchValue={searchValue}
                            />
                        </div>
                    </Box>
                </Box>
                <Box className="md:hidden" display="flex" justifyContent="center" mt={2}>
                    <div className="w-full">
                        <SearchBar
                            isMobile
                            onDateRangeChange={handleDateRangeChange}
                            placeholder={"Search with Analysis Name..."}
                            setSearchValue={onSearchChange}
                            searchValue={searchValue}
                        />
                    </div>
                </Box>
            </Grid>

            <Grid item xs={12}></Grid>

            <Grid item xs={12}>
                {value === 1 ? (
                    <Box pt={2}>
                        <TableContainer component={Paper} className={classes.tableContainer} style={{ minHeight: (loader || !savedResults?.length) ? "500px" : "auto", overflow: loader ? "hidden" : "auto" }}>
                            {myFilters?.totalPages > myFilters?.limit && (
                                <CustomPagination
                                    onChangePagination={onChangeMyPagination}
                                    filters={myFilters}
                                    totalPage={myFilters?.totalPages}
                                />
                            )}
                            <Table className="universal-table fit-content-mobile">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>No.</TableCell>
                                        <TableCell style={{ cursor: "pointer" }} onClick={() => handleSortClick("name", false)}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                Analysis Name
                                                <div style={{ color: myFilters.sortBy === "name" ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                                                    {myFilters.sortBy === "name" && myFilters.sortDirection === "desc" ? <LuArrowDown size={14} /> : myFilters.sortBy === "name" && myFilters.sortDirection === "asc" ? <LuArrowUp size={14} /> : <LuArrowDownUp size={14} />}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>Vessel Name</TableCell>
                                        <TableCell>First Load Port</TableCell>
                                        <TableCell>Last Discharge Port</TableCell>
                                        <TableCell>Cargo</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell style={{ cursor: "pointer" }} onClick={() => handleSortClick("createdDate", false)}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                Created on
                                                <div style={{ color: myFilters.sortBy === "createdDate" ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                                                    {myFilters.sortBy === "createdDate" && myFilters.sortDirection === "desc" ? <LuArrowDown size={14} /> : myFilters.sortBy === "createdDate" && myFilters.sortDirection === "asc" ? <LuArrowUp size={14} /> : <LuArrowDownUp size={14} />}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>Created By</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isInitialUserLoading && (!Array.isArray(savedResults) || !savedResults.length) ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" className="!bg-white">
                                                <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                                                    {savedResults?.length > 0 && <CircularProgress />}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : Array.isArray(savedResults) && savedResults.length ? (
                                        savedResults.map((item, index) => {
                                            return (
                                                <TableRow
                                                    key={index}
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onDoubleClick={() => window.open(`/estimation-analysis/estimation/${item._id}`, "_blank")}
                                                >
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <a
                                                            style={{ color: "blue" }}
                                                            href={`/estimation-analysis/estimation/${item._id}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {item?.name || ""}
                                                        </a>
                                                    </TableCell>
                                                    <TableCell>{item?.vessel?.vesselName || ''}</TableCell>
                                                    <TableCell>
                                                        {item?.clientQuery?.cargoList?.find(item => item.operation?.toLowerCase() === "loading")?.port}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item?.clientQuery?.cargoList?.slice()?.reverse()?.find(item => item.operation?.toLowerCase() === "discharging")?.port}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item?.clientQuery?.cargoList?.find(item => item.operation?.toLowerCase() === "loading")?.cargo}
                                                    </TableCell>
                                                    <TableCell>{item?.clientQuery?.cargoList?.find(item => item.operation?.toLowerCase() === "loading")?.account}</TableCell>
                                                    <TableCell>{moment(item.createdDate).format("DD/MM/YYYY")}</TableCell>
                                                    <TableCell>{item?.createdUserName || ''}</TableCell>
                                                    <TableCell>
                                                        <div className="query-actions">
                                                            <>
                                                                <span className="pending-steper validate-icon-vessel-manager">
                                                                    <ShareIcon color="primary" onClick={() => onShareOrgClick(item)} />
                                                                </span>
                                                                <div className="tooltip-notification-vessel-manager">
                                                                    <p>Share with your Team</p>
                                                                </div>
                                                            </>
                                                            <DeleteIcon color="secondary" onClick={() => onClickDeleteAddress(item?._id)} />
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan="10" align="center" className="!bg-white">
                                                No Estimation analysis Details Found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            {myFilters?.totalPages > myFilters?.limit && (
                                <CustomPagination
                                    onChangePagination={onChangeMyPagination}
                                    filters={myFilters}
                                    totalPage={myFilters?.totalPages}
                                />
                            )}
                        </TableContainer>
                    </Box>
                ) : (
                    <Box pt={2}>
                        <TableContainer component={Paper} className={classes.tableContainer} style={{ minHeight: (isInitialOrgLoading && !allSavedResults?.length) ? "500px" : "auto" }}>
                            {allFilters?.totalPages > allFilters?.limit && (
                                <CustomPagination
                                    onChangePagination={onChangeAllPagination}
                                    filters={allFilters}
                                    totalPage={allFilters?.totalPages}
                                />
                            )}
                            <Table className="universal-table fit-content-mobile">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>No.</TableCell>
                                        <TableCell style={{ cursor: "pointer" }} onClick={() => handleSortClick("name", true)}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                Analysis Name
                                                <div style={{ color: allFilters.sortBy === "name" ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                                                    {allFilters.sortBy === "name" && allFilters.sortDirection === "desc" ? <LuArrowDown size={14} /> : allFilters.sortBy === "name" && allFilters.sortDirection === "asc" ? <LuArrowUp size={14} /> : <LuArrowDownUp size={14} />}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>Vessel Name</TableCell>
                                        <TableCell>First Load Port</TableCell>
                                        <TableCell>Last Discharge Port</TableCell>
                                        <TableCell>Cargo</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell style={{ cursor: "pointer" }} onClick={() => handleSortClick("createdDate", true)}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                Created on
                                                <div style={{ color: allFilters.sortBy === "createdDate" ? "#fff" : "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
                                                    {allFilters.sortBy === "createdDate" && allFilters.sortDirection === "desc" ? <LuArrowDown size={14} /> : allFilters.sortBy === "createdDate" && allFilters.sortDirection === "asc" ? <LuArrowUp size={14} /> : <LuArrowDownUp size={14} />}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>Created By</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isInitialOrgLoading && (!Array.isArray(allSavedResults) || !allSavedResults.length) ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" className="!bg-white">
                                                <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                                                    {allSavedResults?.length > 0 && <CircularProgress />}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : Array.isArray(allSavedResults) && allSavedResults.length ? (
                                        allSavedResults.map((item, index) => {
                                            return (
                                                <TableRow
                                                    key={index}
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onDoubleClick={() => window.open(`/estimation-analysis/estimation/${item?._id}`, "_blank")}
                                                >
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <a
                                                            style={{ color: "blue" }}
                                                            href={`/estimation-analysis/estimation/${item?._id}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {item?.name || ""}
                                                        </a>
                                                    </TableCell>
                                                    <TableCell>{item?.vessel?.vesselName || ''}</TableCell>
                                                    <TableCell>
                                                        {item?.clientQuery?.cargoList?.find(item => item.operation?.toLowerCase() === "loading")?.port}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item?.clientQuery?.cargoList?.slice()?.reverse()?.find(item => item.operation?.toLowerCase() === "discharging")?.port}
                                                    </TableCell>
                                                    <TableCell>
                                                        {item?.clientQuery?.cargoList?.find(item => item.operation?.toLowerCase() === "loading")?.cargo}
                                                    </TableCell>
                                                    <TableCell>{item?.clientQuery?.cargoList?.find(item => item.operation?.toLowerCase() === "loading")?.account}</TableCell>
                                                    <TableCell>{moment(item.createdDate).format("DD/MMM/yyyy")}</TableCell>
                                                    <TableCell>{item?.createdUserName || ''}</TableCell>
                                                </TableRow>
                                            )
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan="9" align="center" className="!bg-white">
                                                No Estimation analysis Details Found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            {allFilters?.totalPages > allFilters?.limit && (
                                <CustomPagination
                                    onChangePagination={onChangeAllPagination}
                                    filters={allFilters}
                                    totalPage={allFilters?.totalPages}
                                />
                            )}
                        </TableContainer>
                    </Box>
                )}
            </Grid>

            <CustomConfirmModal
                open={deleteModal}
                handleClose={() => setDeleteModal(false)}
                message="Are you sure you want to delete this Estimation analysis?"
                onSubmitConfirm={onDeleteVesselAnalysis}
                type="warning"
                confirmText="DELETE"
                cancelText="Cancel"
                note="This action cannot be undone."
            />
            <CustomAddModal
                open={isShareConfirm}
                handleClose={onShareOrgClick}
                onSubmitConfirm={onShareOrgSaveClick}
                maxWidth="xs"
                saveLabel="Share"
                title="Share with Team"
            >
                <ShareWithOrg
                    value={shareLabel}
                    onChange={(e) => setShareLabel(e.target.value)}
                    placeholder="Enter Estimation analysis name*"
                    label="Team copy name"
                    guidanceTitle="Share this estimation analysis"
                    guidance="Create a team copy of this estimation analysis. Your team can find it in Estimation Analysis → All Estimation analysis."
                />
            </CustomAddModal>
        </Grid>
    );
}

export default VesselAnalysisListing;
